<template>
  <div class="form-floating">
    <vue-tel-input
      class="form-control pj-input"
      :class="[
        { 'is-invalid': errorMessage },
        { 'pj-ss-input fs-3': isSelfService },
      ]"
      @keyup="updateInput"
      @change="phoneChanged"
      @input="$emit('input', $event.target.value)"
      aria-describedby="help-phone-number"
      :placeholder="placeholder"
      v-model="formPhoneNumber"
      :validCharactersOnly="true"
      :invalidMsg="errorMessage"
      :defaultCountry="merchantCountry"
      mode="international"
      :autoFormat="!disabled"
      :inputOptions="{
        id: 'phone-number',
        placeholder: placeholder,
        type: 'tel',
        showDialCode: true,
      }"
      :disabled="disabled"
    ></vue-tel-input>

    <label
      v-if="labelName"
      for="phone-number"
      class="form-label"
      :class="{ 'fs-4': isSelfService }"
    >
      {{ labelName }}
    </label>

    <div class="invalid-feedback" v-if="errorMessage">
      {{ errorMessage }}
    </div>
    <div id="help-phone-number" class="form-text"></div>
  </div>
</template>

<script type="module" lang="ts">
import {
  computed,
  defineComponent,
  onUpdated,
  ref,
  watchEffect,
  WritableComputedRef,
} from "vue";
import { useI18n } from "vue-i18n";
import { VueTelInput } from "vue-tel-input";


export default defineComponent({
  name: "NewInputFieldPhone",
  emits: ["input-field-phone-emit", "validate-emit", "input"],
  components: {
    VueTelInput,
  },
  props: {
    labelName: String,
    placeholder: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    merchantCountry: {
      type: String,
      default: "MX",
    },
    statePhoneNumber: {
      type: String,
      default: null,
      required: false,
    },
    inputValue: {
      type: String,
      default: "",
    },
    isSelfService: {
      type: Boolean,
      default: false,
    },
    required: { type: Boolean, default: true },
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const formPhoneNumberValue = ref(props.inputValue);
    const errorMessage = ref("");
    const error = ref(false);
    const minValidLength = ref(0);

    const formPhoneNumber: WritableComputedRef<string> = computed({
      get() {
        return formPhoneNumberValue.value || props.statePhoneNumber || "";
      },
      set(newValue) {
        formPhoneNumberValue.value = newValue;
      },
    });

    const isValidPhoneNumber = (phone: string): boolean => {
      const regExp = /^\d{10,15}$/;
      const allowEmptyStringRegExp = /^\d{10,15}$|^$/;
      const phoneWithoutPlusAndSpaces = phone.replace(/[\s+]/g, ""); // Remove any plus signs and spaces from the input phone number
      return props.required
        ? regExp.test(phoneWithoutPlusAndSpaces)
        : allowEmptyStringRegExp.test(phoneWithoutPlusAndSpaces);
    };

    const unMaskedPhoneNumber = (): string => {
      return "+" + formPhoneNumber.value.replace(/[^0-9]/g, "");
    };

    const validate = (): boolean => {
      if (!formPhoneNumber.value && props.required) {
        errorMessage.value = t("SHARED.PHONE_NUMBER_ERROR_TEXT");
        error.value = true;
        return false;
      } else if (!isValidPhoneNumber(formPhoneNumber.value)) {
        errorMessage.value = t("SHARED.PHONE_ERROR_TEXT_NOT_VALID");
        error.value = true;
        emit("validate-emit", false);
        return false;
      }
      emit("input-field-phone-emit", unMaskedPhoneNumber());
      return true;
    };

    watchEffect(() => {
      if (props.inputValue !== undefined) {
        formPhoneNumberValue.value = props.inputValue;
      }
    });

    const getInputFieldValue = () => {
      return unMaskedPhoneNumber();
    };

    const updateInput = (keyEvent, phoneObject): void => {
      const unMaskedNumber = unMaskedPhoneNumber();
      if (
        minValidLength.value &&
        unMaskedNumber.length < minValidLength.value
      ) {
        formPhoneNumber.value = unMaskedNumber;
        emit("validate-emit", false);
        minValidLength.value = 0;
      }
      if (formPhoneNumber.value != unMaskedNumber) {
        if (!minValidLength.value) {
          minValidLength.value = unMaskedNumber.length;
        }
        emit("validate-emit", true);
      }

      errorMessage.value = "";
      // fix issue for vue-tel-input on vue3
      if (phoneObject?.formatted) {
        formPhoneNumber.value = phoneObject.formatted;
      }
    };

    // fix issue for vue-tel-input on vue3
    let firstload = true;
    onUpdated(() => {
      if (props.statePhoneNumber !== null && firstload) {
        firstload = false;
        formPhoneNumber.value = props.statePhoneNumber;
        updateInput(null, null);
      }
    });

    const phoneChanged = () => {
      validate();
    };

    return {
      t,
      formPhoneNumber,
      errorMessage,
      getInputFieldValue,
      updateInput,
      phoneChanged,
      validate,
    };
  },
});
</script>
<style lang="scss" scoped>
.vue-tel-input {
  border: 2px solid #bdb9c6;
  box-shadow: none;
  border-radius: 5px;
  height: 40px;
  display: flex; //hotfix
  .vti__dropdown {
    width: 60px;
    display: inline-block;
    &:hover {
      background: none;
    }
  }

  .vti__input {
    width: calc(100% - 60px);
  }

  &.disabled {
    background: #e9ecef;
  }
}
</style>
